import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import LogoPage from 'components/pages/LogoPage';

import DividedContent from 'components/common/DividedContent';
import ClosedCaptionsButton from 'components/pages/closedCaptions/ClosedCaptionsButton';
import CueStyleBox from 'components/common/closedCaptions/CueStyleBox';
import { Cue } from 'app-types';
import { FocusSettingsProvider } from 'components/focus/FocusSettingsContext';
import FocusGroup from 'components/focus/FocusGroup';
import Selectable from 'components/focus/Selectable';

import { useFocusManager } from 'hooks/focus';
import textToSpeechManager from 'support/tts/TextToSpeechManager';

import {
  setTextType,
  setTextSize,
  setTextColor,
  setTextOpacity,
  setTextShadow,
  setTextShadowColor,
  setBackgroundColor,
  setBackgroundOpacity,
  setWindowColor,
  setWindowOpacity,
} from 'actions/closedCaptionSettings';

import {
  enableClosedCaptioning,
  disableClosedCaptioning,
} from 'actions/closedCaptioning';

import {
  TextTypes,
  TextSizes,
  TextColors,
  TextOpacities,
  TextShadows,
  TextShadowColors,
  BackgroundColors,
  BackgroundOpacities,
  WindowColors,
  WindowOpacities,
  InvertedColors,
} from 'models/closedCaptions';

import cueBoxStyles from 'components/common/closedCaptions/CueStyleBox.module.scss';
import { getPageValues } from 'support/analytics/analyticsHelper';
import useTracking from 'hooks/analytics';
import { VideoCtvRedux, ClosedCaptionStyleProps } from 'store-types';
import styles from './ClosedCaptions.module.scss';

const BUTTON_COLUMN_INDEX = 0;

interface MessageDescriptor {
  id: string;
  description: string;
}

interface Messages {
  closedCaptionsLabel: MessageDescriptor;
  previewLabel: MessageDescriptor;
  settingsLabel: MessageDescriptor;
  ttsTurnClosedCaptioningOn: MessageDescriptor;
  ttsTurnClosedCaptioningOff: MessageDescriptor;
  ttsClosedCaptioningIsOn: MessageDescriptor;
  ttsClosedCaptioningIsOff: MessageDescriptor;
  ttsSelected: MessageDescriptor;
}

const messages: Messages = {
  closedCaptionsLabel: {
    id: 'closedCaptions.label',
    description: 'Closed captions label',
  },
  previewLabel: {
    id: 'closedCaptions.label.preview',
    description: 'Closed captions preview label',
  },
  settingsLabel: {
    id: 'closedCaptions.label.settings',
    description: 'Settings label',
  },
  ttsTurnClosedCaptioningOn: {
    id: 'closedCaptions.tts.closedCaptioning.on',
    description: 'tts for turning closed captioning on',
  },
  ttsTurnClosedCaptioningOff: {
    id: 'closedCaptions.tts.closedCaptioning.off',
    description: 'tts for turning closed captioning off',
  },
  ttsClosedCaptioningIsOn: {
    id: 'closedCaptions.tts.closedCaptioning.isOn',
    description: 'tts for closed captioning set to on',
  },
  ttsClosedCaptioningIsOff: {
    id: 'closedCaptions.tts.closedCaptioning.isOff',
    description: 'tts for closed captioning set to off',
  },
  ttsSelected: {
    id: 'closedCaptions.tts.selected',
    description: 'TTS for selected value',
  },
};

interface PageData {
  pageType: string;
  [key: string]: unknown;
}

type TrackerFunction = (pageData: PageData) => void;

const trackClosedCaptions = (tracker: TrackerFunction): void => {
  const pageData: PageData = {
    pageType: 'configuration',
    ...getPageValues('captions'),
  };

  tracker(pageData);
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
type SettingActionCreator = Function;

interface CCSetting {
  id: keyof ClosedCaptionStyleProps;
  values: readonly string[];
  action: SettingActionCreator;
  type?: 'color';
}

const ccSettings: CCSetting[] = [
  { id: 'textType', values: TextTypes, action: setTextType },
  { id: 'textSize', values: TextSizes, action: setTextSize },
  { id: 'textColor', values: TextColors, action: setTextColor, type: 'color' },
  { id: 'textOpacity', values: TextOpacities, action: setTextOpacity },
  { id: 'textShadow', values: TextShadows, action: setTextShadow },
  {
    id: 'textShadowColor',
    values: TextShadowColors,
    action: setTextShadowColor,
    type: 'color',
  },
  {
    id: 'backgroundColor',
    values: BackgroundColors,
    action: setBackgroundColor,
    type: 'color',
  },
  {
    id: 'backgroundOpacity',
    values: BackgroundOpacities,
    action: setBackgroundOpacity,
  },
  {
    id: 'windowColor',
    values: WindowColors,
    action: setWindowColor,
    type: 'color',
  },
  {
    id: 'windowOpacity',
    values: WindowOpacities,
    action: setWindowOpacity,
  },
];

interface PreviewCue extends Cue {
  lineAlign: string;
}

interface ValueItem {
  text: string;
  id: string;
}

const ClosedCaptions: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const focusManager = useFocusManager();
  const focusGroupRef = useRef<FocusGroup|null>(null);

  const [selectedColumnIndex, setSelectedColumnIndex] = useState<number | string>(0);
  const [selectedSettingIndex, setSelectedSettingIndex] = useState<number|string>(0);
  const valuedSelectedTts = useRef<string | null>(null);

  const currentSettings = useSelector<VideoCtvRedux, ClosedCaptionStyleProps>((state: VideoCtvRedux) => state.closedCaptionSettings);
  const closedCaptionsLabel = intl.formatMessage(messages.closedCaptionsLabel);
  const isCcOn = useSelector<VideoCtvRedux, boolean>((state: VideoCtvRedux) => state.closedCaptioning);
  const isInitialized = useRef<boolean>(false);

  const previewCue: PreviewCue = {
    align: 'center',
    line: 'auto',
    lineAlign: 'start',
    position: 'auto' as unknown as number,
    size: 100,
    snapToLines: true,
    text: intl.formatMessage(messages.previewLabel),
    vertical: '',
    startTime: 0,
    endTime: 0,
  };

  useEffect(() => {
    const onFocusChangedListener = (newId: string, prevId: string): void => {
      const { isFocusAncestor } = focusManager;
      const focusedTTS = textToSpeechManager.getTTSText(newId);
      if (!focusedTTS || !isFocusAncestor('closed-captions-page')) return;

      !isInitialized.current && textToSpeechManager.add(closedCaptionsLabel);

      const settingsItemRegex = /\bcc-setting-([^-]+)/;
      if (newId.match(settingsItemRegex) && !prevId.match(settingsItemRegex)) {
        textToSpeechManager.add(intl.formatMessage(messages.settingsLabel));
      }

      textToSpeechManager.add(focusedTTS);

      // TTS logic for mouse clicks
      if (valuedSelectedTts.current && isFocusAncestor('cc-values-column'))
        textToSpeechManager.add(valuedSelectedTts.current);
      valuedSelectedTts.current = null;

      textToSpeechManager.speak();
    };
    focusManager.addFocusChangedListener(onFocusChangedListener);
    return () => {
      focusManager.removeFocusChangedListener(onFocusChangedListener);
    };
  }, [closedCaptionsLabel, focusManager, intl]);

  const isButtonColumnSelected = (): boolean =>
    selectedColumnIndex === BUTTON_COLUMN_INDEX;

  const getTtsForCcButton = useCallback(
    (isOnButton: boolean): string =>
      intl.formatMessage(
        isOnButton
          ? messages[
              isCcOn ? 'ttsClosedCaptioningIsOn' : 'ttsTurnClosedCaptioningOn'
            ]
          : messages[
              isCcOn ? 'ttsTurnClosedCaptioningOff' : 'ttsClosedCaptioningIsOff'
            ],
      ),
    [intl, isCcOn],
  );

  useEffect(() => {
    focusManager.setCurrentFocus('closed-captions-page');
  }, [focusManager]);

  // This useEffect is responsible for calling out the closed captions enabled state when changed
  const wasCcOn = useRef<boolean>(isCcOn);
  useEffect(() => {
    const { currentFocusId } = focusManager;
    if (isInitialized.current && isCcOn !== wasCcOn.current) {
      const ttsText = getTtsForCcButton(currentFocusId === 'cc-button-on');
      textToSpeechManager.add(ttsText);
      textToSpeechManager.speak();
    }
    wasCcOn.current = isCcOn;
  });

  useEffect(() => {
    isInitialized.current = true;
  }, []);

  const { trackPage: tracker } = useTracking();
  useEffect(() => {
    trackClosedCaptions(tracker);
  }, [tracker]);

  const renderButtonColumn = (): React.ReactNode => {
    return (
      <FocusGroup
        focusId="cc-button-column"
        className={styles.buttonColumn}
        data-testid="cc-button-column"
        onFocusSetOverride={() => (isCcOn ? 0 : 1)}
      >
        <ClosedCaptionsButton
          focusId="cc-button-on"
          onClick={() => {
            dispatch(enableClosedCaptioning());
          }}
          isOnButton
          isSelected={isCcOn}
          ttsText={getTtsForCcButton(true)}
        />
        <ClosedCaptionsButton
          focusId="cc-button-off"
          onClick={() => {
            dispatch(disableClosedCaptioning());
          }}
          isSelected={!isCcOn}
          ttsText={getTtsForCcButton(false)}
        />
      </FocusGroup>
    );
  };

  const renderSettingsColumn = (): React.ReactNode => {
    const renderSettingsItem = (item: CCSetting, index: number): React.ReactNode => {
      const isSelected =
        selectedSettingIndex === index && !isButtonColumnSelected();
      const settings = ccSettings[index];
      const settingId = settings?.id;
      const message = {
        id: `closedCaptions.settings.label.${settingId}`,
        description: 'Closed captions label',
      };
      const text = intl.formatMessage(message);
      return (
        <Selectable
          key={item.id}
          focusId={`cc-setting-${index}`}
          className={styles.settingsItem}
          ttsText={text}
          render={({ hasFocus }: { hasFocus: boolean }) => (
            <button
              type="button"
              className={classNames(styles.button, {
                [styles.settingItemFocused]: hasFocus,
                [styles.settingItemSelected]: isSelected && !hasFocus,
              })}
            >
              {text}
            </button>
          )}
        />
      );
    };

    return (
      <div className="d-flex flex-column">
        <div className={styles.settingsLabel}>
          {intl.formatMessage(messages.settingsLabel)}
        </div>
        <FocusGroup
          focusId="cc-settings-column"
          className={classNames('d-flex flex-column', styles.settingsColumn)}
          data-testid="cc-settings-column"
          onFocusIndexChanged={setSelectedSettingIndex}
        >
          {ccSettings.map((item, index) => renderSettingsItem(item, index))}
        </FocusGroup>
      </div>
    );
  };

  const renderValuesColumn = (): React.ReactNode => {
    const setting = ccSettings[selectedSettingIndex as number];
    const settingId = setting?.id;
    const settingValues = setting?.values || [];
    const settingType = setting?.type;
    const selectedValue = currentSettings[settingId] as string;
    const selectedValueIndex = settingValues.findIndex(
      item => selectedValue === item,
    );
    const items: ValueItem[] = settingValues.map(item => {
      const message = {
        id: `closedCaptions.settings.${settingId}.${item}`,
        description: 'Closed captions label',
      };
      return {
        text: intl.formatMessage(message),
        id: item,
      };
    });

    const onSelect = (item: ValueItem, index: number): void => {
      dispatch(setting.action(item.id));
      if (focusManager.currentFocusId === `cc-value-${index}`) {
        // No event will be fired for already focused item
        textToSpeechManager.add(intl.formatMessage(messages.ttsSelected));
        textToSpeechManager.speak();
      } else {
        // TTS will be handled when focus changed
        valuedSelectedTts.current = intl.formatMessage(messages.ttsSelected);
      }
    };

    const renderItem = (item: ValueItem, index: number): React.ReactNode => {
      const isSelected = selectedValue === item.id;
      return (
        <Selectable
          key={item.id}
          focusId={`cc-value-${index}`}
          className={
            settingType === 'color'
              ? classNames(
                  cueBoxStyles[`text-color-${item.id}`],
                  styles.colorItem,
                  styles.valuesColumnItem,
                )
              : classNames(styles.stringItem, styles.valuesColumnItem)
          }
          ttsText={item.text}
          onSelect={() => {
            onSelect(item, index);
          }}
          render={({ hasFocus }: { hasFocus: boolean }) => {
            return settingType === 'color' ? (
              <button
                type="button"
                className={classNames('position-relative', styles.button, {
                  [styles.colorItemFocused]: hasFocus,
                })}
                onClick={() => {
                  onSelect(item, index);
                }}
              >
                <div className={styles.colorItemDot} />
                <div
                  className={classNames(styles.colorItemFocusRing, {
                    [styles.colorItemFocusRingFocused]: hasFocus,
                  })}
                />
                {isSelected && (
                  <div
                    className={classNames(
                      styles.check,
                      styles[`check-${InvertedColors[selectedValue as keyof typeof InvertedColors]}`],
                    )}
                  />
                )}
              </button>
            ) : (
              <button
                type="button"
                className={classNames('position-relative', styles.button, {
                  [styles.stringItemFocused]: hasFocus,
                  [styles.stringItemSelected]: isSelected,
                })}
                onClick={() => {
                  onSelect(item, index);
                }}
              >
                <span>{item.text}</span>
                {isSelected && <div className={styles.stringItemDot} />}
              </button>
            );
          }}
        />
      );
    };

    return (
      <div className="d-flex">
        <div className={styles.divider} />
        <div className="d-flex flex-column">
          <FocusGroup
            focusId="cc-values-column"
            className={styles.valuesColumn}
            data-testid="item-rows"
            onFocusSetOverride={() => selectedValueIndex}
          >
            {items.map((item, index) => renderItem(item, index))}
          </FocusGroup>
        </div>
      </div>
    );
  };

  return (
    <FocusSettingsProvider enableTTSOnFocus={false}>
      <LogoPage className="position-relative">
        <DividedContent
          title={<div className={styles.title}>{closedCaptionsLabel}</div>}
        >
          <FocusGroup
            ref={focusGroupRef}
            focusId="closed-captions-page"
            horizontal
            className="d-flex"
            data-testid="closed-captions-page"
            onFocusIndexChanged={setSelectedColumnIndex}
          >
            {renderButtonColumn()}
            {isCcOn && renderSettingsColumn()}
            {isCcOn && !isButtonColumnSelected() && renderValuesColumn()}
          </FocusGroup>
        </DividedContent>
      </LogoPage>
      {isCcOn && (
        <div
          className={styles.cueContainer}
          data-testid="closed-caption-overlay"
        >
          <CueStyleBox
            cue={previewCue}
            settings={currentSettings}
            style={{ bottom: 0 }}
          />
        </div>
      )}
    </FocusSettingsProvider>
  );
};

export default ClosedCaptions;
